import { 
  memberService,
  contributionService,
  loanService,
  distributionService,
  reportService,
  healthService,
  type Member,
  type CreateMemberRequest,
  type UpdateMemberRequest,
  type Contribution,
  type CreateContributionRequest,
  type Loan,
  type CreateLoanRequest,
  type LoanPaymentRequest,
  type Distribution,
  type CreateDistributionRequest
} from './index';
import { MockApiService } from './mockApi';
import {
  convertLoanStatus
} from './typeHelpers';

// Check if mock mode is enabled
const isMockMode = import.meta.env.VITE_MOCK_MODE === 'true';

// Types for pagination
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface MemberFilters extends PaginationParams {
  search?: string;
  status?: string;
  role?: string;
}

export interface ContributionFilters extends PaginationParams {
  year?: number;
  month?: number;
  memberId?: string;
  status?: string;
}

export interface LoanFilters extends PaginationParams {
  memberId?: string;
  status?: string;
}

// Helper to extract data from ApiResponse
function extractData<T>(response: any): T {
  if (!response) {
    throw new Error('No response received');
  }
  if (!response.success) {
    throw new Error(response.error || 'API request failed');
  }
  // Cast data to expected type since the generated types use Record<string, never>
  return response.data as T;
}

// Helper to paginate array
function paginateArray<T>(
  items: T[], 
  page: number = 1, 
  pageSize: number = 20
): PaginatedResponse<T> {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  
  return {
    items: items.slice(startIndex, endIndex),
    total: items.length,
    page,
    pageSize,
    totalPages: Math.ceil(items.length / pageSize)
  };
}

// Wrapper service that switches between real and mock APIs
export class DataService {
  // Member endpoints
  static async getMembers(params?: MemberFilters): Promise<PaginatedResponse<Member>> {
    if (isMockMode) {
      return MockApiService.getMembers(params);
    }
    
    // Real API call
    let members = await memberService.list();
    
    // Apply filters
    if (params?.search) {
      const search = params.search.toLowerCase();
      members = members.filter(m => 
        m.name.toLowerCase().includes(search) ||
        m.phoneNumber?.includes(search) ||
        m.email?.toLowerCase().includes(search)
      );
    }
    
    if (params?.status) {
      members = members.filter(m => m.status === params.status);
    }
    
    if (params?.role) {
      members = members.filter(m => m.role === params.role);
    }
    
    return paginateArray(members, params?.page, params?.pageSize);
  }

  static async getMember(memberId: string): Promise<Member> {
    if (isMockMode) {
      const response = await MockApiService.getMember(memberId);
      return extractData<Member>(response);
    }
    
    return await memberService.get(memberId);
  }

  static async createMember(data: CreateMemberRequest): Promise<Member> {
    if (isMockMode) {
      // Mock API expects different types, so pass original data
      const response = await MockApiService.createMember(data as any);
      return extractData<Member>(response);
    }
    
    return await memberService.create(data);
  }

  static async updateMember(memberId: string, data: UpdateMemberRequest): Promise<Member> {
    if (isMockMode) {
      // Mock API expects different types, so pass original data
      const response = await MockApiService.updateMember(memberId, data as any);
      return extractData<Member>(response);
    }
    
    return await memberService.update(memberId, data);
  }

  static async deleteMember(memberId: string): Promise<void> {
    if (isMockMode) {
      await MockApiService.deleteMember(memberId);
      return;
    }
    
    await memberService.delete(memberId);
  }

  // Contribution endpoints
  static async getContributions(params?: ContributionFilters): Promise<PaginatedResponse<Contribution>> {
    if (isMockMode) {
      return MockApiService.getContributions(params);
    }
    
    // Real API call
    let contributions = await contributionService.list();
    
    // Apply filters
    if (params?.year) {
      contributions = contributions.filter(c => c.year === params.year);
    }
    
    if (params?.month) {
      contributions = contributions.filter(c => c.month === params.month);
    }
    
    if (params?.memberId) {
      contributions = contributions.filter(c => c.memberId === params.memberId);
    }
    
    if (params?.status) {
      contributions = contributions.filter(c => c.status === params.status);
    }
    
    return paginateArray(contributions, params?.page, params?.pageSize);
  }

  static async getContribution(id: string): Promise<Contribution> {
    if (isMockMode) {
      // Mock doesn't have this endpoint, so get all and find
      const all = await MockApiService.getContributions();
      const contribution = all.items.find(c => c.contributionId === id);
      if (!contribution) throw new Error('Contribution not found');
      return contribution;
    }
    
    return await contributionService.get(id);
  }

  static async createContribution(data: CreateContributionRequest): Promise<Contribution> {
    if (isMockMode) {
      // Mock API expects different types, so pass original data
      const response = await MockApiService.createContribution(data as any);
      return extractData<Contribution>(response);
    }
    
    return await contributionService.create(data);
  }

  static async updateContribution(id: string, data: CreateContributionRequest): Promise<Contribution> {
    if (isMockMode) {
      // Mock doesn't have update, so throw error
      throw new Error('Update contribution not available in mock mode');
    }
    
    return await contributionService.update(id, data);
  }

  static async confirmContribution(id: string): Promise<Contribution> {
    if (isMockMode) {
      // Mock doesn't have this endpoint
      throw new Error('Confirm contribution not available in mock mode');
    }
    
    return await contributionService.confirm(id);
  }

  static async deleteContribution(id: string): Promise<void> {
    if (isMockMode) {
      // Mock doesn't have this endpoint
      throw new Error('Delete contribution not available in mock mode');
    }
    
    return await contributionService.delete(id);
  }

  // Loan endpoints
  static async getLoans(params?: LoanFilters): Promise<PaginatedResponse<Loan>> {
    if (isMockMode) {
      const mockResponse = await MockApiService.getLoans(params);
      // Convert mock loan statuses to API statuses
      const convertedLoans = mockResponse.items.map(loan => ({
        ...loan,
        status: convertLoanStatus(loan.status)
      })) as Loan[];
      
      return {
        ...mockResponse,
        items: convertedLoans
      };
    }
    
    let loans = await loanService.list();
    
    // Apply filters
    if (params?.memberId) {
      loans = loans.filter(l => l.memberId === params.memberId);
    }
    
    if (params?.status) {
      loans = loans.filter(l => l.status === params.status);
    }
    
    return paginateArray(loans, params?.page, params?.pageSize);
  }

  static async getLoan(loanId: string): Promise<Loan> {
    if (isMockMode) {
      const response = await MockApiService.getLoan(loanId);
      const loan = extractData<any>(response);
      return {
        ...loan,
        status: convertLoanStatus(loan.status)
      } as Loan;
    }
    
    return await loanService.get(loanId);
  }

  static async createLoan(data: CreateLoanRequest): Promise<Loan> {
    if (isMockMode) {
      // Mock API expects different types, so pass original data
      const response = await MockApiService.createLoan(data as any);
      const loan = extractData<any>(response);
      return {
        ...loan,
        status: convertLoanStatus(loan.status)
      } as Loan;
    }
    
    return await loanService.create(data);
  }

  static async approveLoan(loanId: string): Promise<Loan> {
    if (isMockMode) {
      throw new Error('Approve loan not available in mock mode');
    }
    
    return await loanService.approve(loanId);
  }

  static async rejectLoan(loanId: string): Promise<Loan> {
    if (isMockMode) {
      throw new Error('Reject loan not available in mock mode');
    }
    
    return await loanService.reject(loanId);
  }

  static async recordLoanPayment(loanId: string, data: LoanPaymentRequest): Promise<void> {
    if (isMockMode) {
      // Mock implementation exists
      const mockData = {
        ...data,
        loanId
      };
      await MockApiService.createLoanPayment(mockData as any);
      return;
    }
    
    await loanService.recordPayment(loanId, data);
  }

  // Distribution endpoints
  static async getDistributions(): Promise<Distribution[]> {
    if (isMockMode) {
      // Mock doesn't have distributions
      return [];
    }
    
    return await distributionService.list();
  }

  static async getDistribution(id: string): Promise<Distribution> {
    if (isMockMode) {
      throw new Error('Distributions not available in mock mode');
    }
    
    return await distributionService.get(id);
  }

  static async createDistribution(data: CreateDistributionRequest): Promise<Distribution> {
    if (isMockMode) {
      throw new Error('Create distribution not available in mock mode');
    }
    
    return await distributionService.create(data);
  }

  static async approveDistribution(distributionId: string): Promise<Distribution> {
    if (isMockMode) {
      throw new Error('Approve distribution not available in mock mode');
    }
    
    return await distributionService.approve(distributionId);
  }

  static async executeDistribution(distributionId: string): Promise<Distribution> {
    if (isMockMode) {
      throw new Error('Execute distribution not available in mock mode');
    }
    
    return await distributionService.execute(distributionId);
  }

  // Report endpoints
  static async getContributionReport(): Promise<any> {
    if (isMockMode) {
      // Return mock report data
      return {
        totalContributions: 500000,
        totalMembers: 85,
        averageContribution: 5882,
        monthlyBreakdown: []
      };
    }
    
    return await reportService.contributions();
  }

  static async getFinancialReport(): Promise<any> {
    if (isMockMode) {
      throw new Error('Financial report not available in mock mode');
    }
    
    return await reportService.financial();
  }

  static async getLoanReport(): Promise<any> {
    if (isMockMode) {
      return {
        totalLoans: 10,
        totalAmount: 500000,
        activeLoans: 8,
        overdueLoans: 1
      };
    }
    
    return await reportService.loans();
  }

  static async getSummaryReport(): Promise<any> {
    if (isMockMode) {
      const stats = await MockApiService.getDashboardStats();
      return stats.data;
    }
    
    return await reportService.summary();
  }

  // Health endpoints
  static async healthCheck() {
    if (isMockMode) {
      return MockApiService.healthCheck();
    }
    
    return await healthService.check();
  }

  static async getVersion() {
    if (isMockMode) {
      return MockApiService.getVersion();
    }
    
    return await healthService.version();
  }

  // Utility methods
  static isMockMode(): boolean {
    return isMockMode;
  }

  static clearMockData(): void {
    if (isMockMode) {
      MockApiService.clearMockData();
    }
  }

  static resetMockData(): void {
    if (isMockMode) {
      MockApiService.resetMockData();
    }
  }
}